import React, { useState } from 'react';
import { Search, ShieldCheck, ShieldAlert, ShieldQuestion, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import logger from '@/lib/logger';

/**
 * Hunter.io controls on the Lead Detail card.
 *
 * - "Find Email" — only shown when the lead has no email but does have name +
 *   (website OR company). Clicks `/api/hunter/find-for-lead/{id}`.
 * - "Verify" — only shown when an email exists. Clicks `/api/hunter/verify-for-lead/{id}`.
 * - Verification status badge — shows the last persisted verification result.
 *
 * All consumes a Hunter credit, so we don't auto-fire on render.
 */
export function HunterEmailActions({ lead, onRefresh, isViewer }) {
  const [finding, setFinding] = useState(false);
  const [verifying, setVerifying] = useState(false);
  if (isViewer) return null;

  const hasEmail = !!lead?.email;
  const canFind = !hasEmail && lead?.name && (lead?.website || lead?.company || lead?.email);
  const v = lead?.email_verification;

  const handleFind = async () => {
    if (finding) return;
    setFinding(true);
    try {
      const { data } = await api.hunterFindForLead(lead.id);
      if (!data.email) {
        toast.info('Hunter could not confidently find an email for this lead');
      } else if (data.saved_to_lead) {
        toast.success(`Found email: ${data.email}`, { description: `Confidence: ${data.score ?? '—'}/100` });
      } else {
        toast.success(`Found: ${data.email}`, { description: 'Lead already had an email — not overwritten.' });
      }
      onRefresh?.();
    } catch (e) {
      logger.error(e);
      toast.error(e?.response?.data?.detail || 'Hunter find failed');
    } finally { setFinding(false); }
  };

  const handleVerify = async () => {
    if (verifying) return;
    setVerifying(true);
    try {
      const { data } = await api.hunterVerifyForLead(lead.id);
      const r = data.status || data.result || 'unknown';
      const msg = `Hunter says: ${r} (score ${data.score ?? '—'})`;
      if (r === 'valid' || r === 'deliverable') toast.success(msg);
      else if (r === 'invalid' || r === 'undeliverable') toast.error(msg);
      else toast.warning(msg);
      onRefresh?.();
    } catch (e) {
      logger.error(e);
      toast.error(e?.response?.data?.detail || 'Hunter verify failed');
    } finally { setVerifying(false); }
  };

  const badge = (() => {
    if (!v?.status) return null;
    const s = String(v.status).toLowerCase();
    let cls = 'bg-slate-50 text-slate-600 border-slate-200';
    let Icon = ShieldQuestion;
    if (s === 'valid' || s === 'deliverable') { cls = 'bg-emerald-50 text-emerald-700 border-emerald-200'; Icon = ShieldCheck; }
    else if (s === 'invalid' || s === 'undeliverable') { cls = 'bg-red-50 text-red-700 border-red-200'; Icon = ShieldAlert; }
    else if (s === 'accept_all' || s === 'webmail' || s === 'unknown' || s === 'risky') { cls = 'bg-amber-50 text-amber-700 border-amber-200'; Icon = ShieldQuestion; }
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] font-medium rounded-sm border ${cls}`} title={`Verified ${v.checked_at ? new Date(v.checked_at).toLocaleDateString() : ''} · Score ${v.score ?? '—'}`} data-testid="hunter-verify-badge">
        <Icon className="w-3 h-3" /> {s}{typeof v.score === 'number' ? ` ${v.score}` : ''}
      </span>
    );
  })();

  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-1">
      {badge}
      {hasEmail && (
        <button
          type="button"
          onClick={handleVerify}
          disabled={verifying}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] font-medium rounded-sm border border-[#FF7E0E]/40 text-[#FF7E0E] bg-[#FF7E0E]/5 hover:bg-[#FF7E0E]/10 transition-colors disabled:opacity-60"
          title="Verify deliverability via Hunter.io (1 credit)"
          data-testid="hunter-verify-btn"
        >
          {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
          {verifying ? 'Verifying' : 'Verify'}
        </button>
      )}
      {canFind && (
        <button
          type="button"
          onClick={handleFind}
          disabled={finding}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] font-medium rounded-sm border border-[#0047AB]/40 text-[#0047AB] bg-[#0047AB]/5 hover:bg-[#0047AB]/10 transition-colors disabled:opacity-60"
          title="Find an email via Hunter.io using the lead's name + website/company (1 credit)"
          data-testid="hunter-find-btn"
        >
          {finding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          {finding ? 'Searching' : 'Find email'}
        </button>
      )}
    </div>
  );
}
